import dayjs from "dayjs";
import { COMMON_FIELD_TYPES } from "../constants/fieldTypes";
import { errorMessages } from "../constants/frontendErrorMessages";

// NOTE: Ghana Phone Number Pattern
// const GHANA_PHONE_NUMBER_PATTERN = /^(?:\+233|0)[2356789]\d{8}$/;
const GHANA_PHONE_NUMBER_PATTERN = /^\d{5,}$/;


// Validate MongoDB ObjectId format
export const isValidObjectId = (id) => /^[a-fA-F0-9]{24}$/.test(id);

// Validate email
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return errorMessages.INVALID_EMAIL;
  return "";
};

// FUNCTION: Validate Phone Number
function validatePhone(phoneNumber) {
  // INFO: Ghana Mobile & Landline Numbers
  // Mobile: 10 digits, starting with 02X, 05X, or 024-059
  // Landline: 9 digits, starting with 03X

  if (!phoneNumber) {
    return errorMessages.REQUIRED_FIELD;
  } else if (!GHANA_PHONE_NUMBER_PATTERN.test(phoneNumber)) {
    return errorMessages.INVALID_PHONE_NUMBER;
  }

  return "";
}
// EXAMPLE:
// console.log(validatePhone("+233245678901")); // true
// console.log(validatePhone("0245678901")); // true
// console.log(validatePhone("1234567890")); // false

// Validate email or phone number
const validateEmailOrPhone = (input) => {
  if (!input) {
    return errorMessages.REQUIRED_FIELD;
  } else if (validateEmail(input) === "") {
    // valid email
    return "";
  } else if (validatePhone(input) === "") {
    // valid phone number
    return "";
  }

  return errorMessages.INVALID_EMAIL_OR_PHONE; // invalid email or phone number
};

// Validate password
const validatePassword = (password) => {
  const hasMinLength = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  if (!hasMinLength || !hasUpperCase || !hasLowerCase || !hasSpecialChar) {
    return errorMessages.INVALID_PASSWORD;
  }
  return "";
};

// FUNCTION: Validate number
const isValidNumber = (input) => {
  // Check if the input is of type "number" or can be converted to a valid number
  if (typeof input === "number" && !isNaN(input)) {
    return "";
  }
  if (
    typeof input === "string" &&
    !isNaN(parseFloat(input)) &&
    isFinite(input)
  ) {
    return "";
  }
  return errorMessages.INVALID_NUMBER;
};

// FUNCTION: Validate date
const isValidDate = (dateString) => {

  // console.log("dateString", dateString);

  // If no date provided
  if (!dateString) return false;
  dateString = dayjs(dateString);

  // If it's a dayjs object, format it first
  if (dayjs.isDayjs(dateString)) {
    dateString = dateString.format("YYYY-MM-DD");
    // console.log("dayjs formatted date", dateString);
  }

  // Check format (YYYY-MM-DD)
  const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateFormatRegex.test(dateString)) return false;

  // Parse the date parts
  const [year, month, day] = dateString.split("-").map(Number);

  // Create a new Date object and verify the values
  const date = new Date(year, month - 1, day); // month is 0-based in JavaScript
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set time to start of the day

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    year >= 1900 && // Reasonable minimum year
    year <= 2100 && // Reasonable maximum year
    date >= today // Ensure the date is not in the past
  );
};
// USAGE:
// EXAMPLE:
/*
  console.log(isValidDate('2025-01-25')); // true
  console.log(isValidDate('2025-13-25')); // false (invalid month)
  console.log(isValidDate('2025-02-30')); // false (invalid day)
  console.log(isValidDate('202/02/20'));  // false (wrong format)
  console.log(isValidDate(''));           // false
  console.log(isValidDate(null));         // false
  console.log(isValidDate(dayjs()));      // true (current date)
*/

// FUNCTION: Validate time
const isValidTime = (time) => {
  // Regex for 12-hour format (e.g., 01:30 AM, 12:45 PM)
  const twelveHourRegex = /^(0[1-9]|1[0-2]):([0-5][0-9])\s?(AM|PM)$/i;
  
  // Regex for 24-hour format (e.g., 14:50, 00:15)
  const twentyFourHourRegex = /^([01]\d|2[0-3]):([0-5][0-9])$/;
  
  return twelveHourRegex.test(time) || twentyFourHourRegex.test(time);
};

// FUNCTION: Ghana Nic Number Validation - Format Should be like this GHA-XXXXXXXXX-X
function isValidGhanaIdNumber(pin) {
  // INFO:
  //  A three-letter country code (e.g., 'GHA' for Ghana),
  //  Followed by a hyphen,
  //  Nine digits,
  //  Another hyphen,
  //  And a single check digit.

  // // USAGE: Regular expression to match the Ghana Card PIN format
  // const pinRegex = /^GHA-\d{9}-\d$/;
  // return pinRegex.test(pin);

   // Check if the input is a string and has a minimum length of 1
  if (typeof pin !== "string" || pin.length < 1) {
    return false;
  }
}
// EXAMPLE:
// console.log(validateGhanaCardPIN("GHA-123456789-0")); // Output: true
// console.log(validateGhanaCardPIN("GHA-12345-0")); // Output: false

// FUNCTION: Validate field
export const validateField = (name, type, value, formData, requiredFields) => {

  if (!type) return "Error";

  let error = "";
  const fieldType = typeof type === "object" ? type.type : type; // Extract actual type

  switch (fieldType) {

    case COMMON_FIELD_TYPES.REQUIRED_FIELD:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      break;

    case COMMON_FIELD_TYPES.BOOLEAN_FIELD:
      if (!value && value !== false && requiredFields[name])
        error = errorMessages.REQUIRED_FIELD;
      else if (
        !(!value && value !== false) &&
        !(value === false || value === true)
      )
        error = errorMessages.INVALID_OPTION;
      break;

    case COMMON_FIELD_TYPES.NUMBER_FIELD:
      if (!value && value !== false && requiredFields[name])
        error = errorMessages.REQUIRED_FIELD;
      else if (value) error = isValidNumber(value);
      break;

    case COMMON_FIELD_TYPES.FIRST_NAME:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      break;

    case COMMON_FIELD_TYPES.LAST_NAME:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      break;

    case COMMON_FIELD_TYPES.EMAIL_FIELD:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value) error = validateEmail(value);
      break;

    case COMMON_FIELD_TYPES.PHONE:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value) error = validatePhone(value);
      break;

    case COMMON_FIELD_TYPES.PASSWORD_FIELD:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value) error = validatePassword(value);
      break;

    case COMMON_FIELD_TYPES.CONFIRM_PASSWORD:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value !== formData.password)
        error = errorMessages.PASSWORD_MISMATCH;
      break;

    case COMMON_FIELD_TYPES.DROPDOWN:
      // console.log("dropdown", value, requiredFields[name]);
      if ((!value || Object.keys(value).length === 0) && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      }
      //   else if (typeof value !== "object" || !value.hasOwnProperty("id"))
      //     error = errorMessages.INVALID_OPTION;
      break;

    case COMMON_FIELD_TYPES.DATE_PICKER:
      if (!value && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      } else if (value && !isValidDate(value)) {
        // console.log("trying to validate date", value);
        error = errorMessages.INVALID_DATE;
      }
      break;

    case COMMON_FIELD_TYPES.TIME_PICKER:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (!isValidTime(value)) error = errorMessages.INVALID_TIME;
      break;

    case COMMON_FIELD_TYPES.ID_NUMBER:
      if (!value && requiredFields[name]) {
        // console.log("ID Number is required");
        error = errorMessages.REQUIRED_FIELD;
      } else if (isValidGhanaIdNumber(value)) {
        error = errorMessages.INVALID_ID_NUMBER;
      }
      break;

    case COMMON_FIELD_TYPES.EMAIL_OR_PHONE:
      if (!value && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      } else if (value) {
        error = validateEmailOrPhone(value);
      }
      break;

    default:
      break;
  }

  return error;
};
